import * as d3 from 'd3';
import { ISeries, AreaSeries, LineSeries, RangeSeries, IAxedSeries } from '../pieces/series/index';
import { ILogger, LoggerFactory } from '../../helpers/helpers';
import { INewSeries } from './INewSeries';
import { ISeriesManager } from './ISeriesManager';

export class SeriesManager implements ISeriesManager {
    private _series: ISeries[];
    private _container: d3.Selection<any>;
    private _logger: ILogger;
    private _data: any[];
    private _rawData: any[];

    constructor(private _: _.LoDashStatic, private _d3: typeof d3) {
        this._series = [];
        this._data = [];
        this._logger = LoggerFactory.getLogger('WideTech.Charting.seriesManager');
    }

    container(value?: d3.Selection<any>): d3.Selection<any> {
        if (value) {
            this._container = value;
            this._logger.info('Setting container.');
        }
        return this._container;
    }

    updateSeries(newSeriesList: INewSeries[]) {
        this._logger.info('Updating series');
        try {
            const toRemove = this._.filter(this._series, s => !this._.find(newSeriesList, ns => ns.identifier === s.identifier()));
            toRemove.forEach(s => {
                this.remove(s);
            });
            const self = this;
            const series = [];
            newSeriesList.forEach(ns => {
                const existing: any = this.getExisting(self, ns);
                this.update(ns, existing);
                series.push(existing);
            });
            this._series = series;
            const barSeries = this._series.filter((s: any) => s.isBarsVisible && s.isBarsVisible());
            barSeries.forEach((s: any, i) => s.updateBars(i, barSeries.length));
            this._logger.info('Series updated.');
        } catch (e) {
            this._logger.error(e);
        }
    }

    private getExisting(self: this, ns: INewSeries) {
        let existing: any = this._.find(self._series, s => s.identifier() === ns.identifier);
        if (!existing) {
            if (!ns.seriesType || ns.seriesType === 'line') {
                existing = self.addLineSeries(ns.name, ns.color, ns.value, ns.identifier, ns.selector, null, ns.serieState);
                existing.isLineVisible((ns.isLineVisible === null || ns.isLineVisible === undefined || ns.isLineVisible)
                    && (existing.serieState === 0 || existing.serieState === 2));
            } else if (ns.seriesType === 'area') {
                existing = self.addAreaSeries(ns.name, ns.color, ns.y0, ns.y1, ns.identifier, ns.selector, ns.serieState);
                if (ns.crosshairFormat) {
                    existing.crosshairFormat(ns.crosshairFormat);
                }
                existing.isLineVisible((ns.isLineVisible === null || ns.isLineVisible === undefined || ns.isLineVisible)
                    && (existing.serieState === 0 || existing.serieState === 2));
            } else if (ns.seriesType === 'range') {
                existing = self.addRangeSeries(ns.name, ns.color, ns.identifier, ns.selector, ns.value, ns.title, ns.serieState);
            }
        }
        return existing;
    }

    series(): ISeries[] {
        return this._series;
    }

    min(): number {
        const mins = this._series.filter((s: any) => !(s.hasOwnAxis && s.hasOwnAxis())).map(s => s.min());
        return this._d3.min(mins);
    }

    max(): number {
        const maxs = this._series.filter((s: any) => !(s.hasOwnAxis && s.hasOwnAxis())).map(s => s.max());
        return this._d3.max(maxs);
    }

    private addLineSeries(seriesName: string, color: string,
        value: { (data: any): number }, identifier, selector,
        symbol?, state?: number): ISeries {
        this._logger.debug('Creating new lineSeries.');
        const series = new LineSeries(this._container, seriesName, color, value, identifier, selector);
        if (symbol) {
            series.symbol(symbol);
            series.isSymbolVisible(true);
        }
        if (state) {
            series.setSerieState(false, state);
        }
        this.add(series);
        this._logger.debug('lineSeries created.');
        return series;
    }

    private addAreaSeries(seriesName: string, color: string,
        y0: { (data: any): number }, y1: { (data: any): number },
        identifier, selector, state?: number): ISeries {
        this._logger.debug('Creating new areaSeries.');
        const series = new AreaSeries(this._container, seriesName, color, y0, y1, identifier, selector);
        if (state) {
            series.setSerieState(false, state);
        }
        this.add(series);
        this._logger.debug('areaSeries created.');
        return series;
    }

    private addRangeSeries(seriesName: string, color: string, identifier, selector, data, title, state?: number): ISeries {
        this._logger.debug('Creating new rangeSeries.');
        const series = new RangeSeries(d3.select((this._container[0][0] as any).parentNode),
            seriesName, color, identifier, selector, data, title);
        if (state) {
            series.setSerieState(false, state);
        }
        this.add(series);
        this._logger.debug('rangeSeries created.');
        return series;
    }

    private add(series: IAxedSeries) {
        this._series.push(series);
        if (this._rawData) {
            const current = this._rawData.filter(d => series.selector()(d) === series.identifier());
            series.data(current);
        }
        if (this._postAdd) {
            this._logger.debug('postAdd');
            this._postAdd(series);
        }
    }

    private remove(series: ISeries) {
        const found = this._.find(this._series, s => s.identifier() === series.identifier());
        if (found) {
            found.remove();
            this._.remove(this._series, s => s.identifier() === series.identifier());
            if (this._postRemove) {
                this._postRemove(found);
            }
        }
    }

    private update(ns: any, existing: any) {
        try {
            //TODO make an update per series
            if (existing.isSymbolVisible) {
                existing.isSymbolVisible(ns.isSymbolVisible);
            }
            existing.color(ns.color);
            if (existing.interpolate) {
                existing.interpolate(ns.renderingMode);
            }
            if (existing.symbol) {
                existing.symbol(this._d3.svg.symbol().type(ns.symbol));
            }
            if (existing.isValueLabelsVisible) {
                existing.isValueLabelsVisible(ns.isValueLabelsVisible);
            }
            existing.legendText(ns.name);
            if (existing.lineStyle) {
                existing.lineStyle(ns.lineStyle);
            }
            if (ns.coloringConditions) {
                existing.coloringConditions(ns.coloringConditions);
            }
            if (existing.hasOwnAxis) {
                existing.hasOwnAxis(!!ns.hasOwnAxis);
            }
            if (existing.yMin) {
                existing.yMin(ns.yMin);
            }
            if (existing.yMax) {
                existing.yMax(ns.yMax);
            }
            existing.pane = ns.pane;
            existing.isVisible(ns.isVisible);
            if (existing.isLineVisible) {
                existing.isLineVisible(ns.isLineVisible);
            }
            if (existing.isBarsVisible) {
                existing.isBarsVisible(ns.isBarsVisible);
            }
            if (ns.serieState !== null && ns.serieState !== undefined) {
                if (ns.isLineVisible && ns.isBarsVisible && existing.setOriginalVisibility) {
                    existing.setOriginalVisibility(ns.isLineVisible, ns.isBarsVisible);
                }
                if (ns.serieState === 1) {
                    existing.setSerieState(false, 1);
                }
            }
            existing.isCrosshairHidden = ns.isCrosshairHidden;
            existing.click = ns.click;
            existing.isStacked(ns.isStacked);
            if (existing.legendAttributes) {
                existing.legendAttributes(ns.attributes);
            }
            if (existing.crosshairFormat) {
                existing.crosshairFormat(ns.crosshairFormat);
            }
            if (this._postUpdate) {
                this._postUpdate(existing);
            }
        } catch (e) {
            this._logger.error(e);
        }
    }

    private _postAdd;

    postAdd(value?: any): any {
        if (value) {
            this._postAdd = value;
        }
        return this._postAdd;
    }

    private _postUpdate;

    postUpdate(value?) {
        if (value) {
            this._postUpdate = value;
        }
        return this._postUpdate;
    }

    private _postRemove;

    postRemove(value?) {
        if (value) {
            this._postRemove = value;
        }
        return this._postRemove;
    }

    data(data?: any[]): any[] {
        if (arguments.length) {
            this._rawData = data;
            let currentData = [];
            this._series.forEach((s: any) => {
                const current = data.filter(d => s.selector()(d) === s.identifier());
                s.data(current);
                currentData = currentData.concat(current);
            });
            this._data = currentData;
        }
        return this._data;
    }
}